'use client';

import { FC } from 'react';
import '@shared/styles/container.scss';
import { TypeMegaMenuComponentWithoutUnresolvableLinksResponse } from '@shared/features/contentful/_generated';
import FooterBottomMenu from './FooterBottomMenu';
import FooterFollowUs from './FooterFollowUs';
import FooterDownloadApp from '@shared/components/SharedFooter/FooterDownloadApp';
import FooterMenu from './FooterMenu';
import {
    useContentfulInspectorMode,
    useContentfulLiveUpdates,
} from '@contentful/live-preview/react';
import styles from './Footer.module.scss';
import FooterLogo from './FooterLogo';

interface FooterProps {
    data: TypeMegaMenuComponentWithoutUnresolvableLinksResponse;
}

const Footer: FC<FooterProps> = ({ data }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: data.sys.id });
    const liveData = useContentfulLiveUpdates(data);

    const { appLinks, bottomLinks, copyrightText, items, socialLinks, logoLink } = liveData.fields;
    return (
        <footer
            className={`border-t border-neutral-200 bg-white pt-[30px] pb-[30px] md:pt-[80px] md:pb-[80px] ${styles.footer}`}
        >
            <div className="mx-auto w-full max-w-[1440px] px-4 lg:px-12">
                <FooterLogo logoLink={logoLink?.fields.link} />

                <hr className="border-neutral-200 my-[30px] md:my-[40px]" />

                {/* Desktop layout */}
                <div className="hidden md:flex justify-between" data-testid="footer-desktop">
                    <FooterMenu menuItems={items} />
                    <div className="flex flex-col gap-[40px] w-[248px] shrink-0">
                        <FooterFollowUs socialLinks={socialLinks} contentfulEntryId={data.sys.id} />
                        <FooterDownloadApp apps={appLinks} contentfulEntryId={data.sys.id} />
                    </div>
                </div>

                {/* Mobile layout */}
                <div className="md:hidden flex flex-col gap-10" data-testid="footer-mobile">
                    <div className="grid grid-cols-2 gap-4">
                        <FooterMenu menuItems={items?.slice(0, 2)} isMobile />
                    </div>
                    {(items?.length ?? 0) > 2 && (
                        <FooterMenu menuItems={items?.slice(2)} isMobile />
                    )}
                    <div className="flex flex-col gap-10">
                        <FooterFollowUs socialLinks={socialLinks} contentfulEntryId={data.sys.id} />
                        <FooterDownloadApp apps={appLinks} contentfulEntryId={data.sys.id} />
                    </div>
                </div>

                <hr className="border-neutral-200 my-[40px]" />

                <FooterBottomMenu links={bottomLinks} />
                <p
                    className="text-[14px] text-colourForegroundSecondary mb-[9px] md:mb-[34px] mt-[40px] font-[400]"
                    {...inspectorProps({ fieldId: 'copyrightText' })}
                >
                    {copyrightText}
                </p>
            </div>
        </footer>
    );
};

export default Footer;
